/**
 * Dashboard — Home (v77.99.102, iter 122.home3).
 *
 * The application's front door. Calm editorial layout with deliberate,
 * meaningful colour — every accent communicates state, not decoration.
 *
 * Built entirely from the existing CoParent Console design system:
 *   • Typography:  Work Sans (`font-heading`) + IBM Plex Sans + `font-mono-data`
 *   • Surfaces:    white cards with hairline `#D9E1E3` borders, `rounded-md`
 *                  on `#F5F7F8` page background.
 *   • Colour-with-purpose palette (see TONE_STYLES below):
 *       Teal   #1F9FA4 / #116B6E   — primary navigation, messages, confirmed
 *       Blue   #4A6170 / #E6ECEF   — calendar, information
 *       Green  #1F9FA4 / #D6F0EF   — money owed to you, confirmed visits
 *       Amber  #B88A3A / #FBF0DC   — needs attention, awaiting confirmation
 *       Red    #C0291D / #FBE6E3   — genuine issues (disputes, overdue)
 *       Purple #6B4F8B / #E9E4F1   — school events
 *
 * Answers four questions and nothing more:
 *   1. What do I need to do today?
 *   2. Is anything waiting for me?
 *   3. What is happening with my children?
 *   4. What has changed since I last opened the app?
 *
 * Data: GET /api/dashboard/home — single composed call.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
// v77.99.108e (iter 122.icon-audit) — all module icons come from the
// canonical registry so a Dashboard widget visually previews its
// destination module. Replaced Handshake / PoundSterling that
// previously only appeared on this page and nowhere else, which
// broke the cross-app visual language.
import {
  MODULE_ICONS,
  CalendarDays as CalendarIcon,
  ShieldCheck,
  Wallet,
  MessageSquare,
  GraduationCap,
  Stethoscope,
} from "@/lib/moduleIcons";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OnboardingChecklist from "@/components/OnboardingChecklist";

// ---------- Tone tokens --------------------------------------------------
// One source of truth. Every coloured surface or dot maps to a tone, and
// every tone maps to existing brand hex codes — no new colours.

const TONE_STYLES = {
  calm:      { bg: "#EEF3F4", border: "#D9E1E3", text: "#2E3A3F", dot: "#9DB0B5" },
  info:      { bg: "#E6ECEF", border: "#B6C5CC", text: "#2E3E48", dot: "#4A6170" },
  positive:  { bg: "#D6F0EF", border: "#A7D8DA", text: "#0F5658", dot: "#1F9FA4" },
  attention: { bg: "#FBF0DC", border: "#E2C588", text: "#6F4E1E", dot: "#B88A3A" },
  alert:     { bg: "#FBE6E3", border: "#E8B6AF", text: "#8C1F12", dot: "#C0291D" },
};

const EVENT_TONE_DOT = {
  calendar:                  "#4A6170", // blue
  school:                    "#6B4F8B", // purple
  medical:                   "#1F9FA4", // teal
  contact_visit_confirmed:   "#1F9FA4", // green/teal (confirmed)
  contact_visit_awaiting:    "#B88A3A", // amber (awaiting)
};

// ---------- helpers ------------------------------------------------------

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "·";

// v77.99.108e (iter 122.icon-audit) — `handshake` and `money` now map
// to the canonical module icons (ShieldCheck for Contact Visits,
// Wallet for Expenses). Backend may still emit those legacy slugs;
// the visual is what matters.
const SUMMARY_ICONS = {
  calendar:  CalendarIcon,
  handshake: ShieldCheck,
  money:     Wallet,
  message:   MessageSquare,
  school:    GraduationCap,
  medical:   Stethoscope,
};

// ---------- atoms --------------------------------------------------------

/** Big section heading, matches "Past settlements" on Expenses. */
function SectionHeading({ children, testId }) {
  return (
    <h2
      className="font-heading text-lg font-medium text-[#2E3A3F] mb-3"
      data-testid={testId}
    >
      {children}
    </h2>
  );
}

/** White surface card with hairline border — the app's standard. */
function SurfaceCard({ className = "", children, testId, style }) {
  return (
    <div
      className={"bg-white border border-[#D9E1E3] rounded-md " + className}
      style={style}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/** Coloured status pill — used on child cards and elsewhere. */
function StatusPill({ tone = "calm", children, testId }) {
  const t = TONE_STYLES[tone] || TONE_STYLES.calm;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-sm"
      style={{ backgroundColor: t.bg, color: t.text, border: `1px solid ${t.border}` }}
      data-testid={testId}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: t.dot }}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}

// ---------- sub-components -----------------------------------------------

function Greeting({ data, banner }) {
  if (!data) {
    return (
      <header className="px-4 sm:px-6 pt-6" data-testid="dashboard-greeting">
        <div className="h-3 w-32 bg-[#EEF3F4] rounded-sm animate-pulse" />
        <div className="h-6 w-44 bg-[#EEF3F4] rounded-sm animate-pulse mt-2" />
      </header>
    );
  }
  const { time_of_day, first_name, today_display } = data;
  const t = banner ? (TONE_STYLES[banner.tone] || TONE_STYLES.calm) : null;
  return (
    <header className="px-4 sm:px-6 pt-6" data-testid="dashboard-greeting">
      <div
        className="text-xs uppercase tracking-[0.12em] text-[#6F828A]"
        data-testid="dashboard-today-display"
      >
        {today_display}
      </div>
      <h1
        className="font-heading text-xl sm:text-2xl font-medium text-[#2E3A3F] mt-1"
        data-testid="dashboard-greeting-line"
      >
        {time_of_day}{first_name ? `, ${first_name}` : ""}
      </h1>
      {banner ? (
        <div
          className="mt-3 px-3.5 py-2 rounded-sm text-sm flex items-center gap-2.5"
          style={{
            backgroundColor: t.bg,
            color: t.text,
            border: `1px solid ${t.border}`,
          }}
          data-testid="dashboard-status-banner"
        >
          <span
            className="h-2 w-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: t.dot }}
            aria-hidden="true"
          />
          <span className="leading-snug flex-1">{banner.text}</span>
          {banner.action?.href ? (
            <Link
              to={banner.action.href}
              className="ml-2 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-sm bg-white/70 hover:bg-white border whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{ borderColor: t.border, color: t.text }}
              data-testid="dashboard-status-banner-action"
            >
              {banner.action.label} →
            </Link>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}

/** Four compact summary cards beneath the greeting (≈76px tall). */
function SummaryCards({ cards }) {
  if (!cards || cards.length === 0) return null;
  return (
    <div
      className="px-4 sm:px-6 mt-5 grid grid-cols-2 gap-2.5"
      data-testid="dashboard-summary-cards"
    >
      {cards.map((c) => {
        const t = TONE_STYLES[c.tone] || TONE_STYLES.calm;
        const Icon = SUMMARY_ICONS[c.icon] || CalendarIcon;
        return (
          <Link
            key={c.key}
            to={c.href}
            className="bg-white border border-[#D9E1E3] rounded-md px-3 py-2.5 flex items-start gap-2.5 hover:bg-[#F4FAFA] transition-colors"
            data-testid={`dashboard-summary-card-${c.key}`}
          >
            <span
              className="h-7 w-7 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: t.bg, color: t.text }}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                {c.label}
              </span>
              <span
                className="block text-sm font-medium leading-snug mt-0.5 truncate"
                style={{ color: t.text }}
                data-testid={`dashboard-summary-card-${c.key}-value`}
              >
                {c.value}
              </span>
              {c.sub ? (
                <span
                  className="block text-[10px] text-[#6F828A] mt-0.5 truncate"
                  data-testid={`dashboard-summary-card-${c.key}-sub`}
                >
                  {c.sub}
                </span>
              ) : null}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

function Today({ events, hasSchedule }) {
  if (!events) return null;
  if (events.length === 0) {
    return (
      <section className="px-4 sm:px-6 mt-8" data-testid="dashboard-today">
        <SectionHeading testId="dashboard-today-heading">Today</SectionHeading>
        <SurfaceCard className="p-4 sm:p-5" testId="dashboard-today-empty">
          <p className="text-sm text-[#6F828A] leading-relaxed">
            {hasSchedule
              ? "Today is clear. Nothing scheduled."
              : "Today is clear. Once you add events they will appear here."}
          </p>
        </SurfaceCard>
      </section>
    );
  }
  return (
    <section className="px-4 sm:px-6 mt-8" data-testid="dashboard-today">
      <SectionHeading testId="dashboard-today-heading">Today</SectionHeading>
      <SurfaceCard className="px-4 sm:px-5 py-1" testId="dashboard-today-list-card">
        <ul className="divide-y divide-[#D9E1E3]" data-testid="dashboard-today-list">
          {events.map((ev) => {
            const dot = EVENT_TONE_DOT[ev.tone] || EVENT_TONE_DOT.calendar;
            return (
              <li
                key={ev.id || `${ev.time}-${ev.title}`}
                className="py-3 flex items-baseline gap-3"
                data-testid="dashboard-today-event"
              >
                <span
                  className="h-2 w-2 rounded-full flex-shrink-0 self-center"
                  style={{ backgroundColor: dot }}
                  aria-hidden="true"
                />
                <span
                  className="font-mono-data text-[13px] text-[#6F828A] w-12 flex-shrink-0"
                  data-testid="dashboard-today-event-time"
                >
                  {ev.time}
                </span>
                <span
                  className="text-sm text-[#2E3A3F] font-medium leading-snug"
                  data-testid="dashboard-today-event-title"
                >
                  {ev.title}
                </span>
              </li>
            );
          })}
        </ul>
      </SurfaceCard>
    </section>
  );
}

function NeedsAttention({ items, total }) {
  if (!items || items.length === 0) return null;
  const shown = items.slice(0, 3);
  const overflow = (total || items.length) - shown.length;
  return (
    <section className="px-4 sm:px-6 mt-8" data-testid="dashboard-attention">
      <SectionHeading testId="dashboard-attention-heading">Needs attention</SectionHeading>
      <SurfaceCard className="overflow-hidden" testId="dashboard-attention-card">
        <ul className="divide-y divide-[#D9E1E3]" data-testid="dashboard-attention-list">
          {shown.map((it, idx) => {
            const severity = it.severity === "red" ? "alert" : "attention";
            const t = TONE_STYLES[severity];
            return (
              <li
                key={(it.type || "item") + idx}
                className="px-4 sm:px-5 py-3.5 border-l-[3px]"
                style={{
                  backgroundColor: `${t.bg}66`, // ~40% opacity tint
                  borderLeftColor: t.dot,
                }}
                data-testid="dashboard-attention-item"
                data-severity={severity}
              >
                <p className="text-sm text-[#2E3A3F] leading-snug">{it.title}</p>
                {it.link ? (
                  <Link
                    to={it.link}
                    className="text-xs font-medium mt-1.5 inline-block"
                    style={{ color: t.text }}
                    data-testid="dashboard-attention-review"
                  >
                    Review →
                  </Link>
                ) : null}
              </li>
            );
          })}
        </ul>
        {overflow > 0 ? (
          <Link
            to="/app/requests"
            className="block px-4 sm:px-5 py-2.5 text-xs font-medium text-[#116B6E] hover:bg-[#F4FAFA] border-t border-[#D9E1E3] transition-colors"
            data-testid="dashboard-attention-view-all"
          >
            View all ({total}) →
          </Link>
        ) : null}
      </SurfaceCard>
    </section>
  );
}

function ChildDot({ name, color }) {
  const bg = color && /^#?[0-9a-fA-F]{6}$/.test(color)
    ? (color.startsWith("#") ? color : `#${color}`)
    : "#1F9FA4";
  return (
    <div
      className="h-10 w-10 rounded-full flex items-center justify-center text-white text-xs font-medium shadow-sm"
      style={{ backgroundColor: bg }}
      aria-hidden="true"
    >
      {initials(name)}
    </div>
  );
}

function childStatusToneAndLabel(c) {
  // Map server status_tone → pill tone + display label.
  const tone = c.status_tone || "calm";
  if (tone === "with_you") return { pillTone: "positive", label: "With you" };
  if (tone === "with_coparent") return { pillTone: "info", label: c.care_status || "With co-parent" };
  if (tone === "handover_today") return { pillTone: "attention", label: "Handover today" };
  return { pillTone: "calm", label: "No visit scheduled" };
}

function Children({ kids }) {
  if (!kids || kids.length === 0) return null;
  const many = kids.length >= 3;
  return (
    <section className="px-4 sm:px-6 mt-8" data-testid="dashboard-children">
      <SectionHeading testId="dashboard-children-heading">Your children</SectionHeading>
      <div
        className={
          many
            ? "flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-4 sm:-mx-6 px-4 sm:px-6"
            : "grid grid-cols-2 gap-3"
        }
        data-testid="dashboard-children-list"
      >
        {kids.map((c) => {
          const { pillTone, label } = childStatusToneAndLabel(c);
          return (
            <SurfaceCard
              key={c.id}
              className={"p-5 " + (many ? "min-w-[190px] snap-start flex-shrink-0" : "")}
              testId="dashboard-child-card"
            >
              <ChildDot name={c.name} color={c.color} />
              <p
                className="text-sm text-[#2E3A3F] font-medium mt-4"
                data-testid="dashboard-child-name"
              >
                {c.name}
              </p>
              <div className="mt-2.5">
                <StatusPill tone={pillTone} testId="dashboard-child-pill">
                  {label}
                </StatusPill>
              </div>
              <p
                className="text-xs text-[#6F828A] mt-3 leading-snug font-mono-data"
                data-testid="dashboard-child-handover"
              >
                {c.next_handover || "No handover scheduled."}
              </p>
            </SurfaceCard>
          );
        })}
      </div>
    </section>
  );
}

function ThisWeek({ data }) {
  if (!data) return null;
  const rows = [
    {
      key: "calendar", label: "Calendar", icon: MODULE_ICONS.calendar,
      value: data.calendar, sub: null, tone: "info",
      href: "/app/calendar",
    },
    {
      key: "money", label: "Money", icon: MODULE_ICONS.expenses,
      value: data.money, sub: null, tone: "positive",
      href: "/app/expenses",
    },
    {
      key: "contact_visits", label: "Contact visits", icon: MODULE_ICONS.contact_visits,
      value: data.contact_visits, sub: null, tone: "attention",
      href: "/app/contact-visits",
    },
    {
      key: "messages", label: "Messages", icon: MODULE_ICONS.messages,
      value: data.messages, sub: null, tone: "calm",
      href: "/app/messages",
    },
  ].filter((r) => r.value);
  if (rows.length === 0) return null;
  return (
    <section className="px-4 sm:px-6 mt-8" data-testid="dashboard-this-week">
      <SectionHeading testId="dashboard-this-week-heading">This week</SectionHeading>
      <SurfaceCard className="px-2" testId="dashboard-this-week-card">
        <ul className="divide-y divide-[#D9E1E3]" data-testid="dashboard-this-week-list">
          {rows.map((r) => {
            const t = TONE_STYLES[r.tone] || TONE_STYLES.calm;
            const Icon = r.icon;
            // Split value into primary / sub on ` · ` for the two-line layout.
            const parts = (r.value || "").split(" · ");
            const primary = parts[0];
            const sub = parts.slice(1).join(" · ");
            return (
              <li key={r.key} data-testid={`dashboard-this-week-${r.key}`}>
                <Link
                  to={r.href}
                  className="flex items-center gap-3 px-3 py-3 hover:bg-[#F4FAFA] transition-colors rounded-sm"
                >
                  <span
                    className="h-8 w-8 rounded-sm flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: t.bg, color: t.text }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.7} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-[#2E3A3F] font-medium">
                      {r.label}
                    </span>
                    <span className="block text-xs text-[#6F828A] leading-snug mt-0.5">
                      {primary}
                      {sub ? <span className="block">{sub}</span> : null}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </SurfaceCard>
    </section>
  );
}

function RecentActivity({ items }) {
  // Group items by `when` label, preserving order. Subtle date headers
  // appear once per group; individual items lose their per-row dates.
  const grouped = useMemo(() => {
    if (!items) return [];
    const out = [];
    let current = null;
    for (const it of items) {
      const w = it.when || "Earlier";
      if (!current || current.when !== w) {
        current = { when: w, items: [] };
        out.push(current);
      }
      current.items.push(it);
    }
    return out;
  }, [items]);
  if (!items || items.length === 0) return null;
  return (
    <section className="px-4 sm:px-6 mt-8" data-testid="dashboard-recently">
      <SectionHeading testId="dashboard-recently-heading">Recent activity</SectionHeading>
      <div data-testid="dashboard-recently-list">
        {grouped.map((g, gi) => (
          <div key={g.when + gi} className="mb-4 last:mb-0">
            <div
              className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] mb-1.5"
              data-testid="dashboard-recently-group-label"
            >
              {g.when}
            </div>
            <ul className="divide-y divide-[#D9E1E3]">
              {g.items.map((it, idx) => (
                <li
                  key={gi + "-" + idx}
                  className="py-2.5 flex items-start gap-2.5"
                  data-testid="dashboard-recently-item"
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-[#9DB0B5] mt-2 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <p className="text-sm text-[#2E3A3F] leading-snug flex-1">
                    {it.text}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <Link
        to="/app/activity"
        className="text-sm text-[#116B6E] hover:text-[#0F5658] mt-3 inline-block font-medium"
        data-testid="dashboard-recently-view-all"
      >
        View full activity →
      </Link>
    </section>
  );
}

function SetupHint({ data }) {
  if (!data) return null;
  if (data.has_children && data.has_schedule) return null;
  const step = data.next_step;
  let title = "Welcome.";
  let body = "Let's get a few essentials in place so the dashboard becomes useful.";
  let cta = null;
  if (step === "add_child" || !data.has_children) {
    title = "Add your first child.";
    body = "Your dashboard will surface what matters once your household is set up.";
    cta = { href: "/app/children", label: "Add a child" };
  } else if (step === "set_schedule" || !data.has_schedule) {
    title = "Set up a parenting schedule.";
    body = "Once a schedule is in place, the dashboard can show care status and handovers automatically.";
    cta = { href: "/app/calendar", label: "Open Calendar" };
  } else if (step === "log_first_visit") {
    title = "Record your first contact visit.";
    body = "Visit records build a calm, secure history over time.";
    cta = { href: "/app/contact-visits", label: "Open Contact Visits" };
  }
  return (
    <section className="px-4 sm:px-6 mt-8" data-testid="dashboard-setup-hint">
      <SectionHeading testId="dashboard-setup-heading">Get started</SectionHeading>
      <SurfaceCard className="p-5" testId="dashboard-setup-card">
        <h3 className="font-heading text-base font-medium text-[#2E3A3F]">{title}</h3>
        <p className="text-sm text-[#6F828A] mt-2 leading-relaxed">{body}</p>
        {cta ? (
          <Link
            to={cta.href}
            className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-sm bg-[#1F9FA4] hover:bg-[#178085] text-white text-xs font-medium transition-colors"
            data-testid="dashboard-setup-cta"
          >
            {cta.label} →
          </Link>
        ) : null}
      </SurfaceCard>
    </section>
  );
}

// ---------- main page ----------------------------------------------------

export default function Dashboard() {
  useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // v77.99.150 (iter 122.onboarding-restore) — The dismissible
  // "Finish setting up your account" checklist was lost when the
  // Dashboard was refactored in v77.99.99. Restore it here: fetch
  // /onboarding/status independently of the main dashboard payload so
  // it renders as soon as it's available, and let OnboardingChecklist
  // handle its own show/hide via `status.show_checklist`.
  const [onboarding, setOnboarding] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/dashboard/home")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Could not load dashboard");
        }
      });
    api
      .get("/onboarding/status")
      .then((r) => {
        if (!cancelled) setOnboarding(r.data);
      })
      .catch((err) => {
        // Non-fatal: dashboard still renders without the checklist.
        console.warn("[dashboard] onboarding status failed:", err?.response?.status || err?.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div
        className="max-w-2xl mx-auto px-4 sm:px-6 py-12"
        data-testid="dashboard-error"
      >
        <p className="text-sm text-[#2E3A3F]">{String(error)}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-24" data-testid="dashboard-page">
      <Greeting data={data?.greeting} banner={data?.status_banner} />
      {onboarding?.show_checklist ? (
        <div className="px-4 sm:px-6 mt-5">
          <OnboardingChecklist status={onboarding} onDismiss={() => setOnboarding((s) => ({ ...(s || {}), show_checklist: false }))} />
        </div>
      ) : null}
      {data ? (
        <>
          <SummaryCards cards={data.summary_cards} />
          <Today
            events={data.today_events}
            hasSchedule={data.setup?.has_schedule}
          />
          <NeedsAttention
            items={data.needs_attention}
            total={data.attention_total}
          />
          <Children kids={data.children} />
          <ThisWeek data={data.this_week} />
          <RecentActivity items={data.recently} />
          <SetupHint data={data.setup} />
        </>
      ) : null}
    </div>
  );
}
